import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';

export interface PresignedUrlResponse {
  success: boolean;
  message: string;
  data: PresignedUrlData;
  errors: string[];
}

export interface PresignedUrlData {
  url: string;
}

interface UrlCache {
  region: string;
  bucket: string;
  key: string;
  response: any;
  timestamp: number;
}

@Injectable({
  providedIn: 'root',
})
export class PresignedUrlService {
  downloadUrlCache: UrlCache[] = [];

  constructor(private _httpClient: HttpClient, private _auth: AuthService) {}

  async generateUploadUrl(
    region: string = environment.s3.region,
    bucket: string = environment.s3.bucket,
    key: string,
    contentType?: string
  ): Promise<PresignedUrlResponse> {
    const headers = await this._auth.generateRequestHeaders();
    const body: { region: string; bucket: string; key: string; contentType?: string } = {
      region,
      bucket,
      key,
    };
    if (contentType) {
      body.contentType = contentType;
    }
    return firstValueFrom(
      this._httpClient.post<PresignedUrlResponse>(
        `${environment.api.baseUrl}/presigned-url/generate-upload`,
        body,
        { headers }
      )
    );
  }

  private checkCache(region: string, bucket: string, key: string): any {
    if (region === undefined || bucket === undefined || key === undefined)
      return undefined;
    const cacheHit = this.downloadUrlCache
      .filter(
        (cache) =>
          cache.key === key &&
          cache.bucket === bucket &&
          cache.region == region &&
          cache.timestamp > new Date().getMilliseconds()
      )
      .pop();
    return cacheHit?.response;
  }

  private addToCache(
    region: string,
    bucket: string,
    key: string,
    response: any
  ) {
    const cachedItem: UrlCache = {
      region: region,
      bucket: bucket,
      key: key,
      response: response,
      timestamp: new Date().getMilliseconds(),
    };
    if (response.success === true) this.downloadUrlCache.push(cachedItem);
  }

  async generateDownloadUrl(
    region: string = environment.s3.region,
    bucket: string = environment.s3.bucket,
    key: string
  ): Promise<PresignedUrlResponse> {
    const cacheHit = this.checkCache(region, bucket, key);
    if (cacheHit !== undefined) return cacheHit;
    let headers = await this._auth.generateRequestHeaders();
    let options = {
      headers,
    };
    const body = {
      region: region,
      bucket: bucket,
      key: key,
    };
    const result = await firstValueFrom(
      this._httpClient.post<PresignedUrlResponse>(
        `${environment.api.baseUrl}/presigned-url/generate-download`,
        body,
        options
      )
    );
    this.addToCache(region, bucket, key, result);
    return result;
  }

  generateGuidV4(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
      /[xy]/g,
      function (char: string) {
        const random = (Math.random() * 16) | 0;
        const v = char === 'x' ? random : (random & 0x3) | 0x8;
        return v.toString(16);
      }
    );
  }

  /**
   * Returns whether the object exists at the given S3 key (HEAD, then ranged GET).
   * Step 1 uses HttpClient; step 2 uses fetch without auth headers.
   */
  async prescriptionFileExists(
    region: string = environment.s3.region,
    bucket: string = environment.s3.bucket,
    key: string
  ): Promise<boolean> {
    try {
      const { data } = await this.generateDownloadUrl(region, bucket, key);
      if (!data?.url) {
        return false;
      }

      let res = await fetch(data.url, { method: 'HEAD' });
      if (res.ok) {
        return true;
      }
      if (res.status === 404) {
        return false;
      }

      res = await fetch(data.url, {
        method: 'GET',
        headers: { Range: 'bytes=0-0' },
      });
      if (res.status === 404) {
        return false;
      }
      return res.ok || res.status === 206;
    } catch {
      return false;
    }
  }

  /**
   * PUT to S3 via presigned URL. Uses fetch (not HttpClient) so auth interceptors
   * do not attach Cognito headers to the S3 request.
   */
  uploadFileToS3(signedUrl: string, file: File): Promise<Response> {
    const contentType = file.type || 'application/pdf';
    return fetch(signedUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': contentType },
    });
  }
}
